import type { AdminFundUpdateEntry } from '../../models/FundUpdateEntry';
import { todayDateString } from './fundUpdatesAdminUtils';

export interface FundUpdatesAdminPanelProps {
  fundOpportunityId: string;
  fundName: string;
  onToolbarWidthChange?: (width: number) => void;
}

export interface HighlightEditState {
  title: string;
  summary: string;
  imageUrl: string;
  /** Images from AI suggestion, carried forward into edit mode */
  availableImages?: string[];
}

export interface HighlightSuggestion {
  id: string;
  title: string;
  summary: string;
  imageUrl: string | null;
  availableImages: string[];
}

export interface DeleteConfirmState {
  update: AdminFundUpdateEntry;
  message: string;
}

export interface GenerateConfirmState {
  update: AdminFundUpdateEntry;
  useProModel: boolean;
  refinementPrompt?: string;
}

export interface FundUpdatesAdminState {
  loading: boolean;
  error: string | null;
  drafts: AdminFundUpdateEntry[];
  published: AdminFundUpdateEntry[];
  expandedIds: Set<string>;
  editingPublishedIds: Set<string>;
  creatingDraft: boolean;
  duplicatingIds: Set<string>;
  newDraftDate: string;
  savingDateIds: Set<string>;
  publishing: boolean;
  discarding: boolean;
  deleteConfirm: DeleteConfirmState | null;
  deleteInProgress: boolean;
  highlightOpenIds: Set<string>;
  highlightEditingId: string | null;
  highlightEdits: Record<string, HighlightEditState>;
  highlightGeneratingIds: Set<string>;
  highlightGeneratingProIds: Set<string>;
  cmsBusyIds: Set<string>;
  saveForGenerateIds: Set<string>;
  saveForGenerateProIds: Set<string>;
  highlightSuggestion: HighlightSuggestion | null;
  highlightRefinementPrompts: Record<string, string>;
  generateConfirm: GenerateConfirmState | null;
  reverting: boolean;
  /** Bumped per update id after a revert, forcing that update's editor to remount and re-fetch its (now-reverted) content. */
  revertNonce: Record<string, number>;
  publishingAll: boolean;
  revertingAll: boolean;
}

export function createInitialFundUpdatesAdminState(): FundUpdatesAdminState {
  return {
    loading: true,
    error: null,
    drafts: [],
    published: [],
    expandedIds: new Set(),
    editingPublishedIds: new Set(),
    creatingDraft: false,
    duplicatingIds: new Set(),
    newDraftDate: todayDateString(),
    savingDateIds: new Set(),
    publishing: false,
    discarding: false,
    deleteConfirm: null,
    deleteInProgress: false,
    highlightOpenIds: new Set(),
    highlightEditingId: null,
    highlightEdits: {},
    highlightGeneratingIds: new Set(),
    highlightGeneratingProIds: new Set(),
    cmsBusyIds: new Set(),
    saveForGenerateIds: new Set(),
    saveForGenerateProIds: new Set(),
    highlightSuggestion: null,
    highlightRefinementPrompts: {},
    generateConfirm: null,
    reverting: false,
    revertNonce: {},
    publishingAll: false,
    revertingAll: false,
  };
}
